import React, { useRef, useEffect, useMemo } from "react";
import { attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toggleGlobalSound, closeMenu, closeSymbolPopup } from "../../../../State/actions";
import { selectSceneTransitionActive } from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import ButtonShadow from "./ButtonShadow";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
// import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
// import { PixiSettings } from "../../../../pixiSettings"; // (layout: useSoundButtonLayout)
import { soundManager } from "../../../../SoundManager";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import { useSoundButtonLayout } from "./useSoundButtonLayout";

const ButtonSound = ({ toolbar, zIndex = 0 }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { globalSoundDisabled } = useSelector((state) => state.sound);
  const sceneTransitionActive = useSelector(selectSceneTransitionActive);
  // const { getResponsiveConfig, device, orientation, isMobileSmall } = useDimensions();
  const { device } = useDimensions();

  // Shared hover/press hook
  const { buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters } =
    useButtonBrightness();

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);

  // Determine which icon to show
  const iconName = globalSoundDisabled
    ? "icon_sound_off"
    : "icon_sound_on";
  const texture = getNewUITexture(iconName);

  // // Override shared config — sound button is slightly smaller on mobile portrait
  // const SOUND_BUTTON_CONFIG = useMemo(() => ({
  //   ...USER_INTERFACE_BUTTON_CONFIG,
  //   DEVICE_CONFIGS: {
  //     ...USER_INTERFACE_BUTTON_CONFIG.DEVICE_CONFIGS,
  //     [PixiSettings.DEVICE.MOBILE]: {
  //       ...USER_INTERFACE_BUTTON_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.MOBILE],
  //       [PixiSettings.ORIENTATION.PORTRAIT]: {
  //         deviceMultiplier: 2.2,
  //       },
  //     },
  //   },
  // }), []);

  // // Get responsive configuration from context - memoized to prevent recalculation
  // const buttonConfig = useMemo(
  //   () => getResponsiveConfig(SOUND_BUTTON_CONFIG, true),
  //   [getResponsiveConfig, SOUND_BUTTON_CONFIG]
  // );

  // // Calculate responsive button position based on device and orientation - memoized
  // const buttonPosition = useMemo(() => {
  //   if (isMobileSmall) {
  //     if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
  //       return {
  //         x: toolbar.width - buttonConfig.width * 0.925,
  //         y: buttonConfig.height * 1.3,
  //       };
  //     } else {
  //       return {
  //         x: buttonConfig.width * 0.6,
  //         y: buttonConfig.height * 0.6,
  //       };
  //     }
  //   }
  //   if (device === PixiSettings.DEVICE.MOBILE) {
  //     if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
  //       return {
  //         x: toolbar.width - buttonConfig.width * 0.85,
  //         y: buttonConfig.height * 1.6,
  //       };
  //     } else {
  //       return {
  //         x: buttonConfig.width * 0.6,
  //         y: buttonConfig.height * 0.7,
  //       };
  //     }
  //   } else if (device === PixiSettings.DEVICE.TABLET) {
  //     if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
  //       return {
  //         x: toolbar.width - buttonConfig.width * 0.8,
  //         y: buttonConfig.width * 0.8,
  //       };
  //     } else {
  //       return {
  //         x: toolbar.width - buttonConfig.width * 0.8,
  //         y: buttonConfig.width * 0.8,
  //       };
  //     }
  //   } else {
  //     // desktop
  //     return {
  //       x: toolbar.width - buttonConfig.width,
  //       y: buttonConfig.width * 0.8,
  //     };
  //   }
  // }, [device, orientation, toolbar.width, buttonConfig.width, buttonConfig.height, isMobileSmall]);

  // The size and the position now come from ONE place, shared with the Clock
  // (it sits beside this button at a fixed gap) — useSoundButtonLayout.
  const { buttonConfig, buttonPosition } = useSoundButtonLayout(toolbar);

  const handleClick = () => {
    // Prevent interaction during scene transitions to avoid music race condition
    if (sceneTransitionActive) {
      return;
    }

    // Close menu if open
    dispatch(closeMenu());
    dispatch(closeSymbolPopup());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Press animation via shared hook
    triggerPress({
      targetWidth: buttonConfig.width,
      targetHeight: buttonConfig.height,
    });

    dispatch(toggleGlobalSound());

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupFilters();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [cleanupFilters]);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width]
  );

  // Determine if button should be disabled (during scene transition)
  const isButtonDisabled = sceneTransitionActive;

  // Calculate button opacity - reduce when disabled
  const buttonOpacity = useMemo(
    () => (isButtonDisabled ? 0.5 : 1.0),
    [isButtonDisabled]
  );

  return (
    <Container
      name="secondary_button_sound"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={zIndex}
      alpha={buttonOpacity}
    >
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_sound_interactive_area"
        eventMode={isButtonDisabled ? "none" : "static"}
        cursor={isButtonDisabled ? "default" : "pointer"}
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      {/* Soft drop shadow under it (ButtonShadow, utils/buttonShadow) */}
      <ButtonShadow target={buttonRef} texture={texture} width={buttonConfig.width} height={buttonConfig.height} />
      <Sprite
        ref={buttonRef}
        texture={texture}
        name="button_sound_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        accessibleTitle={
          globalSoundDisabled ? t("enable_sound") : t("disable_sound")
        }
        accessibleHint={
          globalSoundDisabled ? t("enable_sound") : t("disable_sound")
        }
        roundPixels={true}
      />
    </Container>
  );
};

export default ButtonSound;
